/**
 * state-configuration-service
 *
 * @summary Configure routes, states of application
 * @version 0.1
 * @since 0.1
 * @author Arian Khosravi <arian.khosravi@aofl.com>
 * @module aofl-js/0-6-0/state-configuration-service
 */
define(function() {
  'use strict';

  /**
   * Service allow to configure states
   * @function stateConfigurationService
   * @param {Boolean} applicationDebug - prod/debug state of app
   * @return {Object} stateConfigurationService
   */
  var stateConfigurationService = function stateConfigurationService(
    applicationDebug
  ) {
    /**
     * fileName - depend on applicationDebug, change name of receivable file
     * @private
     * @type {String}
     */
    var fileName = applicationDebug ? 'index.js' : 'index.min.js';

    /**
     * Define main states
     * @function setMain
     * @public
     * @param {Object} stateConfig - state config
     * @param {Object} stateProvider - states provider
     * @param {Object} couchPotatoProvider - couchPotato provider
     */
    var setMain = function setMain(
      stateConfig,
      stateProvider,
      couchPotatoProvider
    ) {
      var states = stateConfig.state;
      var state = null;
      var dependencies = null;

      for (var key in states) {
        if (states.hasOwnProperty(key)) {
          state = states[key];
          dependencies = ['/' + state.path + '/' + fileName];

          stateProvider.state(key, {
            parent: 'root',
            url: state.url,
            path: state.path,
            views: {
              'main@': {
                template: '',
                controller: state.controller,
                resolve: {
                  loadStateCtrl: couchPotatoProvider.resolveDependencies(
                    dependencies
                  )
                }
              }
            }
          });
        }
      }
    };

    /**
     * Define frames of state
     * @function constructFrameViewsObject
     * @private
     * @param {Object} frame - frames object
     * @param {Object} couchPotatoProvider - couchPotato provider
     * @return {Object} views
     */
    var constructFrameViewsObject = function constructFrameViewsObject(
      frame,
      couchPotatoProvider
    ) {
      var views = {};

      if (!(typeof frame === 'object' && frame)) {
        return views;
      }

      for (var templateName in frame) {
        if (frame.hasOwnProperty(templateName)) {
          var stateControllerPath = [frame[templateName].path + '/' + fileName];
          views[templateName + '@'] = {
            template: '',
            controller: frame[templateName].controller,
            resolve: {
              loadStateCtrl: couchPotatoProvider.resolveDependencies(
                stateControllerPath
              )
            }
          };
        }
      }

      return views;
    };

    /**
     * Define root state
     * @function setFrame
     * @public
     * @param {Object} _frame - frames object
     * @param {Object} stateProvider - states provider
     * @param {Object} couchPotatoProvider - couchPotato provider
     */
    var setFrame = function setFrame(
      _frame,
      stateProvider,
      couchPotatoProvider
    ) {
      var frame = _frame || null;
      var views = constructFrameViewsObject(frame, couchPotatoProvider);

      stateProvider.state('root', {
        abstract: true,
        views: views
      });
    };

    /**
     * Define routes
     * @function setRouterStates
     * @public
     * @param {String} applicationId - application Id
     * @param {String} applicationBasePath - base path
     * @param {Object} defaultStates - default states
     * @param {Object} urlRouterProvider - urlRouter provider
     * @param {Object} stateProvider - states provider
     * @param {Object} couchPotatoProvider - couchPotato provider
     */
    var setRouterStates = function setRouterStates(
      applicationId,
      applicationBasePath,
      defaultStates,
      urlRouterProvider,
      stateProvider,
      couchPotatoProvider
    ) {
      // set user specific rotations
      var state = defaultStates;
      var frame = null;
      var redirects = state.when || [];

      // redirect an empty url
      urlRouterProvider.when('', '/');

      redirects.forEach(function(redirect) {
        if (Array.isArray(redirect) && redirect.length === 2) {
          urlRouterProvider.when(redirect[0], redirect[1]);
        } else {
          // eslint-disable-next-line no-console, max-len
          console.warn(
            '[StateConfigurationService] When Redirect Failed to register, expected Array with two values',
            redirect
          );
        }
      });

      if (state.otherwise) {
        urlRouterProvider.otherwise(state.otherwise);
        delete state.otherwise;
      }

      if (state.when) {
        delete state.when;
      }

      // set the frame
      if (typeof state.frame !== 'undefined') {
        frame = state.frame;
        delete state.frame;
      }
      setFrame(frame, stateProvider, couchPotatoProvider);

      // set main content area
      setMain(state, stateProvider, couchPotatoProvider);
    };

    return {
      setMain: setMain,
      setFrame: setFrame,
      setRouterStates: setRouterStates
    };
  };

  return stateConfigurationService;
});
